/**
 * The billing endpoint group.
 *
 * Additive: `GET /payment/subscriptions` and `GET /money/getPaymentDetails`
 * keep working untouched until every client has migrated, and are retired only
 * then. Read-side first, deliberately, so clients can move their display before
 * any write path changes.
 *
 * @see docs/projects/active/refactor-payments/refactor-everything-payments.md — API surface
 */
import { Schema } from 'effect';
import { HttpApiEndpoint, HttpApiGroup, OpenApi } from 'effect/http-api';
import { UserAuthMiddleware } from './middleware';
import {
  BadRequestError,
  CaptchaError,
  InternalServerError,
  PlanChangePreviewStaleError,
  RateLimitedError,
} from './errors';
import { BillingStatus } from './schemas/billing-status';
import {
  CheckoutConfig,
  CheckoutConfigSurface,
} from './schemas/billing-checkout-config';
import { ConfirmResponse } from './schemas/billing-confirm';
import {
  SubscribeRequest,
  SubscribeResponse,
} from './schemas/billing-subscribe';
import {
  CreatePendingCheckoutRequest,
  CreatePendingCheckoutResponse,
  FinishPendingCheckoutResponse,
  PendingCheckoutQuoteQuery,
  PendingCheckoutStatus,
} from './schemas/billing-pending-checkout';
import {
  CancelRequest,
  CancelResponse,
  ChangePlanRequest,
  ChangePlanResponse,
  PlanChangePreview,
  PortalSessionRequest,
  PortalSessionResponse,
  PreviewPlanChangeRequest,
} from './schemas/billing-manage';

/**
 * GET /billing/status
 *
 * One answer to "what does this person have, who bills them, and what may I
 * offer them in-app" — so the client never branches on provider identity.
 */
const getBillingStatusEndpoint = HttpApiEndpoint.get(
  'GetBillingStatus',
  '/status',
  {
    success: BillingStatus,
    error: InternalServerError,
  },
)
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Get billing status')
  .annotate(
    OpenApi.Description,
    'Entitlement, billing relationship and free-tier eligibility for the current user. Capability booleans (can_cancel_in_app, can_change_plan_in_app, manage_url) are the contract — clients must not branch on the provider. The Stripe portal URL is deliberately absent; it is minted on click.',
  );

/**
 * POST /billing/subscribe
 *
 * The single subscription-creation endpoint — it replaced both
 * `create-paid-subscription` and `resubscribe`.
 *
 * The request carries a plan and nothing else that costs money. See
 * `schemas/billing-subscribe.ts` for the fields that are deliberately absent.
 */
const subscribeEndpoint = HttpApiEndpoint.post('Subscribe', '/subscribe', {
  payload: SubscribeRequest,
  headers: Schema.Struct({
    /**
     * The caller's own idempotency key. Optional: without one the server
     * derives a key from the actor, the plan and a 60-second rolling window,
     * which collapses a double-click but not a 3DS failure retried three
     * minutes later. A client that sends its own key gets the firmer guarantee
     * — and it is what lets the endpoint resume the subscription an earlier
     * attempt already created, since it recognises a retry by this exact key.
     *
     * Declared here so the typed client can send it. The header is the only
     * place it can live: it names *this request*, not what is being bought, and
     * a body field would be one more thing a caller could vary per retry.
     *
     * The server namespaces the key by the caller before it reaches Stripe,
     * where keys are account-global — so the same user resending the same key
     * still collapses, but one caller cannot occupy another's keys.
     */
    'idempotency-key': Schema.optional(Schema.String),
  }),
  success: SubscribeResponse,
  error: [BadRequestError, InternalServerError],
})
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Create a subscription')
  .annotate(
    OpenApi.Description,
    'Creates (or uncancels) the caller\'s subscription for a plan. The price, the trial and the confirmation type are all decided server-side: the request carries a plan key, never a price id and never a trial length. Send an "Idempotency-Key" header to make a retry safe; without one the server derives a key from the actor, the plan and a short rolling window.',
  );

/**
 * GET /billing/checkout-config
 *
 * Everything needed to render a paywall, resolved server-side. The client
 * displays what it is given and posts back a `plan_id` — so a price or trial
 * experiment ships without a client change.
 */
const getCheckoutConfigEndpoint = HttpApiEndpoint.get(
  'GetCheckoutConfig',
  '/checkout-config',
  {
    query: Schema.Struct({
      /** A route token that unlocks a promo plan; invalid codes resolve to no promo. */
      promo_code: Schema.optional(Schema.String),
      surface: Schema.optional(CheckoutConfigSurface),
    }),
    success: CheckoutConfig,
    error: InternalServerError,
  },
)
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Get checkout configuration')
  .annotate(
    OpenApi.Description,
    "Resolved plans, trial length, paywall copy variant and publishable key for the current user, plus blocked_reason when a subscription elsewhere means they must not be sold one here. Carries no price ids, lookup keys or variant names. Send surface=paywall only when a paywall is rendered: that request records the experiment exposures and prices plans at the checkout arm; without it nothing is exposed and a Stripe subscriber's plans are priced at the arm a plan change would charge.",
  );

/**
 * POST /billing/confirm
 *
 * The post-redirect sync. Replaced `POST /stripe/successful-payment-intent`.
 */
const confirmEndpoint = HttpApiEndpoint.post('Confirm', '/confirm', {
  success: ConfirmResponse,
  error: [BadRequestError, InternalServerError],
})
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Confirm a checkout')
  .annotate(
    OpenApi.Description,
    "Syncs the caller's subscription from Stripe eagerly, so a client that returns before the webhook lands still sees the truth. Stores a card collected by a SetupIntent as the customer's default, marks the user as having paid, and returns the resolved status alongside the conversion-pixel fields /post-purchase reads.",
  );

/**
 * POST /billing/portal-session
 *
 * A short-lived redirect into Stripe's hosted portal, minted on click. It is a
 * POST rather than a GET because it *creates* a session — five minutes to first
 * use, and no read path may cache one.
 */
const portalSessionEndpoint = HttpApiEndpoint.post(
  'PortalSession',
  '/portal-session',
  {
    payload: PortalSessionRequest,
    success: PortalSessionResponse,
    error: [BadRequestError, InternalServerError],
  },
)
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Mint a billing-portal session')
  .annotate(
    OpenApi.Description,
    'Creates a Stripe billing-portal session against the least-privilege configuration and returns its URL. Money mechanics only: cards and invoices. Cancel and plan change are not portal flows — they are separate endpoints, so the exit survey and save offer cannot be bypassed. The URL is a full redirect (the portal cannot be framed), expires five minutes after minting and must never be cached. Refused for a user with no Stripe customer: a store-billed subscriber gets manage_url from /billing/status instead.',
  );

/**
 * POST /billing/cancel
 *
 * Cancels at period end and keeps the exit survey in our UI. The schedule-bearing
 * cohorts branch inside — Stripe refuses an ordinary deferred cancel on them.
 */
const cancelEndpoint = HttpApiEndpoint.post('Cancel', '/cancel', {
  payload: CancelRequest,
  success: CancelResponse,
  error: [BadRequestError, InternalServerError],
})
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Cancel a subscription')
  .annotate(
    OpenApi.Description,
    "Ends the caller's subscription at the end of the period they have paid for, and returns the instant access actually ends. A schedule-bearing subscriber — an owed-time resubscriber or one of the legacy multi-year prepaid cohort — is cancelled through the schedule instead, because Stripe refuses a deferred cancel on a schedule-managed subscription and an immediate one would destroy prepaid years.",
  );

/**
 * POST /billing/change-plan/preview
 *
 * What confirming a change would do, from Stripe's invoice preview under the
 * same terms the change states. Never cached, hence a POST with a body.
 */
const previewPlanChangeEndpoint = HttpApiEndpoint.post(
  'PreviewPlanChange',
  '/change-plan/preview',
  {
    payload: PreviewPlanChangeRequest,
    success: PlanChangePreview,
    error: [BadRequestError, InternalServerError],
  },
)
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Preview a plan change')
  .annotate(
    OpenApi.Description,
    "Computes what moving the caller's subscription to the named plan would cost, from Stripe's invoice preview under exactly the terms the change itself will state: the amount due today, any credit booked to the customer balance, the previewed lines, the next charge and its date, and the instant the prorations were computed at. The client sends that instant and the amount back on /billing/change-plan, which refuses if the recomputed amount differs. Applies the same refusals as /billing/change-plan.",
  );

/**
 * POST /billing/change-plan
 *
 * Replaced `modify-subscription`, which took any price id the browser sent.
 */
const changePlanEndpoint = HttpApiEndpoint.post('ChangePlan', '/change-plan', {
  payload: ChangePlanRequest,
  headers: Schema.Struct({
    /**
     * The caller's own idempotency key, minted once per confirmed preview so a
     * double-click on one confirmation collapses to one Stripe write. Optional:
     * without one the server derives a key from the actor, the plan and the
     * preview's proration instant, which gives the same guarantee for one
     * preview and a fresh key for the next.
     */
    'idempotency-key': Schema.optional(Schema.String),
  }),
  success: ChangePlanResponse,
  error: [BadRequestError, InternalServerError, PlanChangePreviewStaleError],
})
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Change plan')
  .annotate(
    OpenApi.Description,
    "Moves the caller's subscription to another catalog plan, named by plan key — never by price id, and only against a preview the caller has seen: the request carries the preview's proration instant and amount due today, and the server refuses with a 409 carrying a fresh preview when the recomputed amount differs. Refused for a subscriber whose current price is not a catalog entry (the old plan could not be named as a target to switch back to), for a schedule-bearing subscription, where the update could release the schedule and destroy prepaid time, and for a subscription that has already ended — there is nothing to move, and the paywall is that user's route. Every refusal is one can_change_plan_in_app already advertises on /billing/status.",
  );

const getPendingCheckoutQuoteEndpoint = HttpApiEndpoint.get(
  'GetPendingCheckoutQuote',
  '/pending-checkout/quote',
  {
    query: PendingCheckoutQuoteQuery,
    success: CheckoutConfig,
    error: InternalServerError,
  },
)
  .annotate(OpenApi.Summary, 'Get the pre-signup paywall quote')
  .annotate(
    OpenApi.Description,
    "The paywall a visitor with no account is shown: plans priced at the arm their lis_bucket cookie resolves to, and that arm's trial. Needs no session. Records the pricing exposure under distinct_id when one is sent. blocked_reason is always null — nothing about an account is known yet.",
  );

const createPendingCheckoutEndpoint = HttpApiEndpoint.post(
  'CreatePendingCheckout',
  '/pending-checkout',
  {
    payload: CreatePendingCheckoutRequest,
    success: CreatePendingCheckoutResponse,
    error: [
      BadRequestError,
      CaptchaError,
      RateLimitedError,
      InternalServerError,
    ],
  },
)
  .annotate(OpenApi.Summary, 'Save a card before signup')
  .annotate(
    OpenApi.Description,
    'Starts a pre-signup checkout: verifies the Turnstile token, snapshots the quote for plan_id server-side, and returns the client secret of a SetupIntent that belongs to no Stripe customer. Sets the pending-checkout cookie. Nothing is charged and no subscription exists until an account finishes it.',
  );

const getPendingCheckoutEndpoint = HttpApiEndpoint.get(
  'GetPendingCheckout',
  '/pending-checkout',
  {
    success: PendingCheckoutStatus,
    error: InternalServerError,
  },
)
  .annotate(OpenApi.Summary, 'Read the pending checkout')
  .annotate(
    OpenApi.Description,
    "The pre-signup checkout this browser's cookie names, read live from Stripe: whether a card has been saved, which card, and the quoted plan and trial. state is none when there is no live pending checkout.",
  );

const finishPendingCheckoutEndpoint = HttpApiEndpoint.post(
  'FinishPendingCheckout',
  '/pending-checkout/finish',
  {
    success: FinishPendingCheckoutResponse,
    error: [BadRequestError, InternalServerError],
  },
)
  .middleware(UserAuthMiddleware)
  .annotate(OpenApi.Summary, 'Finish a pre-signup checkout')
  .annotate(
    OpenApi.Description,
    "Creates the signed-in account's subscription on the card saved before signup, at exactly the quoted plan, price and trial. An account that cannot take that offer — already subscribed, trial used, a different pinned price, owed time, a live subscription — is refused with nothing charged and the card left unattached. Idempotent: a retry answers with the subscription the first call created.",
  );

export const billingGroup = HttpApiGroup.make('Billing')
  .add(getBillingStatusEndpoint)
  .add(subscribeEndpoint)
  .add(getCheckoutConfigEndpoint)
  .add(confirmEndpoint)
  .add(portalSessionEndpoint)
  .add(cancelEndpoint)
  .add(previewPlanChangeEndpoint)
  .add(changePlanEndpoint)
  .add(getPendingCheckoutQuoteEndpoint)
  .add(createPendingCheckoutEndpoint)
  .add(getPendingCheckoutEndpoint)
  .add(finishPendingCheckoutEndpoint)
  .prefix('/billing');
